import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  MessageSquare, 
  Database, 
  Download,
  Calendar,
  RefreshCw
} from 'lucide-react'

export function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState('7d')
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Mock analytics data
  const metrics = {
    overview: {
      totalUsers: { value: 1234, change: '+12%', trend: 'up' },
      activeUsers: { value: 856, change: '+8%', trend: 'up' },
      totalConversations: { value: 45678, change: '+23%', trend: 'up' },
      avgSessionTime: { value: '8.5m', change: '-3%', trend: 'down' }
    },
    usage: {
      conversationsToday: 234,
      artifactsCreated: 1456,
      knowledgeBaseQueries: 3421,
      apiCalls: 12567
    },
    popular: {
      agents: [
        { name: 'Support Assistant', conversations: 1247, change: '+15%' },
        { name: 'Content Writer', conversations: 892, change: '+8%' },
        { name: 'Data Analyst', conversations: 234, change: '-2%' }
      ],
      artifactTypes: [
        { name: 'GenerativeImage', count: 456, percentage: 35 },
        { name: 'TodoList', count: 321, percentage: 25 },
        { name: 'StickyNote', count: 298, percentage: 23 },
        { name: 'Chart', count: 218, percentage: 17 }
      ],
      knowledgeBases: [
        { name: 'Product Documentation', queries: 1234, change: '+18%' },
        { name: 'Customer Support FAQ', queries: 987, change: '+12%' },
        { name: 'Company Policies', queries: 456, change: '+5%' }
      ]
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    // TODO: Implement analytics refresh
    setTimeout(() => setIsRefreshing(false), 2000)
  }

  const handleExport = () => {
    // TODO: Implement analytics export
    console.log('Exporting analytics data for range:', timeRange)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600 mt-2">
            Monitor platform usage, performance, and user engagement
          </p>
        </div>
        <div className="flex space-x-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="1d">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw size={16} className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download size={16} className="mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.overview.totalUsers.value}</div>
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              <TrendingUp className={`h-3 w-3 ${
                metrics.overview.totalUsers.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`} />
              <span>{metrics.overview.totalUsers.change} from last period</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <Users className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.overview.activeUsers.value}</div>
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              <TrendingUp className={`h-3 w-3 ${
                metrics.overview.activeUsers.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`} />
              <span>{metrics.overview.activeUsers.change} from last period</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversations</CardTitle>
            <MessageSquare className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.overview.totalConversations.value}</div>
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              <TrendingUp className={`h-3 w-3 ${
                metrics.overview.totalConversations.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`} />
              <span>{metrics.overview.totalConversations.change} from last period</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Session Time</CardTitle>
            <Calendar className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.overview.avgSessionTime.value}</div>
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              <TrendingUp className={`h-3 w-3 ${
                metrics.overview.avgSessionTime.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`} />
              <span>{metrics.overview.avgSessionTime.change} from last period</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Usage</CardTitle>
          <CardDescription>
            Today's platform activity and engagement
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{metrics.usage.conversationsToday}</div>
              <div className="text-sm text-gray-600">Conversations</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{metrics.usage.artifactsCreated}</div>
              <div className="text-sm text-gray-600">Artifacts Created</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{metrics.usage.knowledgeBaseQueries}</div>
              <div className="text-sm text-gray-600">KB Queries</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{metrics.usage.apiCalls}</div>
              <div className="text-sm text-gray-600">API Calls</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Agents */}
        <Card>
          <CardHeader>
            <CardTitle>Top Agents</CardTitle>
            <CardDescription>
              Most active agents by conversation count
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {metrics.popular.agents.map((agent, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-medium text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <div className="font-medium">{agent.name}</div>
                      <div className="text-sm text-gray-600">{agent.conversations} conversations</div>
                    </div>
                  </div>
                  <span className={`text-sm ${
                    agent.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {agent.change}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Popular Artifact Types */}
        <Card>
          <CardHeader>
            <CardTitle>Artifact Types</CardTitle>
            <CardDescription>
              Distribution of created artifact types
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {metrics.popular.artifactTypes.map((type, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{type.name}</span>
                    <span className="text-sm text-gray-600">{type.count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${type.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Knowledge Base Usage */}
      <Card>
        <CardHeader>
          <CardTitle>Knowledge Base Performance</CardTitle>
          <CardDescription>
            Query activity and usage patterns across knowledge bases
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {metrics.popular.knowledgeBases.map((kb, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <Database className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-medium">{kb.name}</div>
                    <div className="text-sm text-gray-600">{kb.queries} queries</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm ${
                    kb.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {kb.change}
                  </div>
                  <div className="text-xs text-gray-500">vs last period</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Chart Placeholder */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Trends</CardTitle>
          <CardDescription>
            Platform activity over time (chart would be implemented here)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <div className="text-center text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-4" />
              <p>Interactive chart component would be rendered here</p>
              <p className="text-sm mt-2">Consider using Chart.js, D3, or Recharts</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}